import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useAppSelector, useAppDispatch } from "@/store/hooks";
import { Chat8MessagesHeader } from "@/components/chat/Chat8MessagesHeader";
import { Chat8InputArea } from "@/components/chat/Chat8InputArea";
import { Chat8StatusBar } from "@/components/chat/Chat8StatusBar";
import { Chat8SessionInfo } from "@/components/chat/Chat8SessionInfo";
import { UniqueMessageList } from "@/components/chat/UniqueMessageList";
import { MessageBubble } from "@/components/chat/MessageBubble";
import { useWebSocket } from "@/contexts/WebSocketContext";
import { logger } from "@/utils/logger";
import { clearEmptyMessages, clearMessages } from "@/store/slices/chatSlice";
import {
  selectWebSocketConnected,
  selectWebSocketError,
  selectWebSocketConnectionHealth,
} from "@/store/slices/websocketSlice";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MessageSquare, X } from "lucide-react";

interface Chat8MessagesModalProps {
  sessionId: string | undefined;
  documentId: string | null;
  onMessageSent?: (message: string) => void;
  onError?: (error: string) => void;
  triggerButton?: React.ReactNode;
}

/**
 * Chat8MessagesModal - Modal para exibir a seção de mensagens do chat inteligente
 * Gerencia header colapsável, lista de mensagens e área de input em um modal
 */
export const Chat8MessagesModal: React.FC<Chat8MessagesModalProps> = React.memo(({
  sessionId,
  documentId,
  onMessageSent,
  onError,
  triggerButton
}) => {
  // Estados locais
  const [isOpen, setIsOpen] = useState(false);
  const [isMessagesExpanded, setIsMessagesExpanded] = useState(true);
  const [inputMessage, setInputMessage] = useState("");
  const [isSending, setIsSending] = useState(false);

  // Hooks do Redux e WebSocket
  const chatState = useAppSelector((state) => state.chat);
  const messages = chatState?.messages || [];
  const { isConnected, sendMessage } = useWebSocket();
  const dispatch = useAppDispatch();

  // ✅ CORRIGIDO: Ler estado real do WebSocket do Redux com verificações de segurança
  const websocketState = useAppSelector((state) => state.websocket);
  const isReconnecting = websocketState?.isReconnecting || false;
  const error = websocketState?.error || null;
  const reconnectAttempts = websocketState?.reconnectAttempts || 0;
  const connectionHealth = websocketState?.connectionHealth?.status || 'unhealthy';

  // ============================================================================
  // EFFECT CRÍTICO: REAGIR A MUDANÇAS DE SESSÃO E DOCUMENTO
  // ============================================================================
  /**
   * useEffect para sessionId e documentId
   * 
   * CRÍTICO: Este effect é executado sempre que sessionId ou documentId mudam
   * Responsabilidades:
   * - Limpar mensagens quando sessão/documento muda
   * - Garantir que mensagens antigas não apareçam na nova sessão
   * - Preparar componente para nova sessão
   * 
   * Dependências: sessionId, documentId, dispatch
   */
  useEffect(() => {
    logger.info(`[Chat8MessagesModal] Sessão/Documento mudou - sessionId: ${sessionId}, documentId: ${documentId}`);
    
    // Limpar mensagens quando sessão ou documento muda
    if (sessionId || documentId) {
      logger.info(`[Chat8MessagesModal] Limpando mensagens para nova sessão/documento`);
      dispatch(clearMessages());
    }
  }, [sessionId, documentId, dispatch]);

  // Limpar mensagens vazias quando o componente for montado
  React.useEffect(() => {
    if (messages.length > 0) {
      // Despachar ação para limpar mensagens vazias
      dispatch(clearEmptyMessages());
    }
  }, []); // Executar apenas uma vez na montagem

  // Calcular contagem de mensagens
  const messagesCount = messages.length;
  const assistantMessageCount = messages.filter(msg => msg.role === 'assistant').length;
  const userMessageCount = messages.filter(msg => msg.role === 'user').length;

  // Callbacks
  const handleToggleMessages = useCallback(() => {
    setIsMessagesExpanded(prev => !prev);
  }, []);

  const handleInputChange = useCallback((value: string) => {
    setInputMessage(value);
  }, []);

  const handleSendMessage = useCallback(
    async (message: string) => {
      if (!message.trim()) return;

      try {
        if (sendMessage) {
          const wsMessage = {
            type: "message",
            content: message,
            document_id: documentId,
          };
          sendMessage(message);
        }
      } catch (error) {
        logger.error("[Chat8MessagesModal] Erro ao enviar mensagem:", error);
        onError?.(error instanceof Error ? error.message : "Erro desconhecido");
      }
    },
    [sendMessage, documentId, onError],
  );

  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSendMessage(inputMessage);
    }
  }, [inputMessage, handleSendMessage]);

  const handleSubmit = useCallback(() => {
    handleSendMessage(inputMessage);
    setInputMessage("");
  }, [inputMessage, handleSendMessage]);

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    if (!open) {
      // Limpar input quando fechar o modal
      setInputMessage("");
    }
  }, []);

  // Memos para performance
  const renderMessage = useCallback((message: any, index: number) => {
    return (
      <MessageBubble
        key={message.id || index}
        message={message}
        isUser={message.role === 'user'}
        variant="default"
        showTimestamp={true}
        showSender={false}
        timestampFormat="time"
      />
    );
  }, []);

  const emptyComponent = useMemo(() => (
    <div className="flex flex-col items-center justify-center h-64 p-8">
      <div className="w-20 h-20 rounded-2xl gradient-primary-br flex items-center justify-center shadow-lg shadow-blue-500/20 mb-6">
        <MessageSquare className="w-10 h-10 text-white" />
      </div>
      <p className="text-lg font-semibold text-platinum mb-2">Nenhuma mensagem ainda</p>
      <p className="text-sm text-silver/70 text-center max-w-md">
        Comece uma conversa com o assistente inteligente. Digite sua mensagem abaixo.
      </p>
    </div>
  ), []);

  // Botão padrão se não for fornecido
  const defaultTriggerButton = useMemo(() => (
    <Button
      variant="outline"
      size="sm"
      className="flex items-center gap-2 bg-navy-700 border-navy text-platinum hover:bg-navy-600"
    >
      <MessageSquare className="h-4 w-4" />
      <span>Histórico de Mensagens</span>
      {messagesCount > 0 && (
        <span className="ml-1 px-2 py-0.5 text-xs bg-blue-600 text-white rounded-full">
          {messagesCount}
        </span>
      )}
    </Button>
  ), [messagesCount]);

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {triggerButton || defaultTriggerButton}
      </DialogTrigger>
      <DialogContent className="max-w-4xl h-[90vh] flex flex-col bg-navy-800 border-navy z-modal">
        <DialogHeader className="flex-shrink-0 pb-4 border-b border-navy bg-navy-700/50">
          <DialogTitle className="text-platinum font-bold">
            Histórico de Mensagens
          </DialogTitle>
          <div className="flex items-center gap-4 text-sm text-silver">
            <span>Total: {messagesCount}</span>
            <span>Usuário: {userMessageCount}</span>
            <span>Assistente: {assistantMessageCount}</span>
          </div>
        </DialogHeader>

        <div className="flex-1 flex flex-col min-h-0 bg-navy-800">
          {/* Header colapsável das mensagens */}
          <Chat8MessagesHeader
            messageCount={messagesCount}
            assistantMessageCount={assistantMessageCount}
            userMessageCount={userMessageCount}
            messagesExpanded={isMessagesExpanded}
            onToggleExpanded={handleToggleMessages}
          />

          {/* Conteúdo das mensagens */}
          <div className="flex-1 flex flex-col min-h-0">
            {/* Informações da sessão */}
            <Chat8SessionInfo
              sessionId={sessionId}
              documentId={documentId}
            />

            {/* Lista de mensagens */}
            {isMessagesExpanded && (
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <UniqueMessageList
                  messages={messages}
                  renderMessage={renderMessage}
                  emptyComponent={emptyComponent}
                  className="space-y-4"
                  onDuplicatesRemoved={(count) => {
                    // Duplicados removidos com sucesso
                  }}
                />
              </div>
            )}

            {/* Área de input */}
            <Chat8InputArea
              input={inputMessage}
              setInput={setInputMessage}
              sending={isSending}
              isConnected={isConnected}
              onSubmit={handleSubmit}
            />

            {/* ✅ CORRIGIDO: Barra de status com dados reais do Redux */}
            <Chat8StatusBar
              isConnected={isConnected}
              isReconnecting={isReconnecting}
              error={error}
              reconnectAttempts={reconnectAttempts}
              connectionHealth={connectionHealth}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
});

Chat8MessagesModal.displayName = 'Chat8MessagesModal'; 